import React from 'react';
import { analyticsService, type AnalyticsDashboard, type AnalyticsRange, type Metric, type StatsSummary } from '../../services/analytics.service';
import { STUDIO_FONT_MONO as FONT_MONO } from '../../views/landing/landingData';
import { EmptyCard, Eyebrow, Icons, RoundBtn, Spinner, Tip } from './studioUi';

interface AnalyticsPanelProps {
    applicationId: string;
}

const RANGES: { id: AnalyticsRange; label: string }[] = [
    { id: '24h', label: '24 hours' },
    { id: '7d', label: '7 days' },
    { id: '30d', label: '30 days' },
    { id: '90d', label: '90 days' },
    { id: '12m', label: '12 months' },
];

/** Data ink: one hue for every chart (single-series views, so no legend needed). */
const DATA = 'var(--hv-leaf)';

const TZ = (() => {
    try {
        return Intl.DateTimeFormat().resolvedOptions().timeZone || 'Asia/Jakarta';
    } catch {
        return 'Asia/Jakarta';
    }
})();

/**
 * Analytics tab: visitors of the PUBLISHED site (subdomain or custom domain),
 * collected by the tracker tenant-api injects into every served page and stored
 * in HyVay's Umami. Built for small-business owners: plain numbers first, lead
 * clicks (WhatsApp / phone / Maps / email) next, then where visitors come from.
 */
export const AnalyticsPanel: React.FC<AnalyticsPanelProps> = ({ applicationId }) => {
    const [range, setRange] = React.useState<AnalyticsRange>('7d');
    const [data, setData] = React.useState<AnalyticsDashboard | null>(null);
    const [loading, setLoading] = React.useState(true);
    const [error, setError] = React.useState<string | null>(null);
    const [online, setOnline] = React.useState<number | null>(null);

    const load = React.useCallback(async (r: AnalyticsRange) => {
        setLoading(true);
        try {
            const d = await analyticsService.dashboard(applicationId, r, TZ);
            setData(d);
            setOnline(d.active ?? null);
            setError(null);
        } catch (e: any) {
            setError(e?.response?.data?.message || 'Could not load analytics. Try again in a moment.');
        }
        setLoading(false);
    }, [applicationId]);

    React.useEffect(() => {
        void load(range);
    }, [load, range]);

    // Realtime badge: visitors in the last 5 minutes, refreshed while the tab is open.
    const live = !!data?.enabled && !!data?.published;
    React.useEffect(() => {
        if (!live) return;
        const t = window.setInterval(() => {
            analyticsService.active(applicationId).then(setOnline).catch(() => undefined);
        }, 30_000);
        return () => window.clearInterval(t);
    }, [applicationId, live]);

    const shell = (children: React.ReactNode) => (
        <div style={{ flex: 1, minWidth: 0, minHeight: 0, overflow: 'auto', padding: 22, display: 'flex', justifyContent: 'center' }}>
            <div style={{ width: '100%', maxWidth: 980, display: 'flex', flexDirection: 'column', gap: 16, alignSelf: 'flex-start' }}>{children}</div>
        </div>
    );

    if (!data) {
        return shell(
            error ? (
                <EmptyCard icon={Icons.chart({ size: 23, sw: 1.8 })} title="Analytics unavailable">{error}</EmptyCard>
            ) : (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10, padding: '60px 0' }}>
                    <Spinner size={14} /><Eyebrow>LOADING ANALYTICS…</Eyebrow>
                </div>
            ),
        );
    }
    if (!data.enabled) {
        return shell(
            <EmptyCard icon={Icons.chart({ size: 23, sw: 1.8 })} title="Analytics is off">
                Visitor analytics is not configured on this server yet (UMAMI_URL). It will start counting once it is.
            </EmptyCard>,
        );
    }
    if (!data.published) {
        return shell(
            <EmptyCard icon={Icons.chart({ size: 23, sw: 1.8 })} title="Publish to see visitors">
                Analytics starts automatically once your site is live, on its HyVay address or your own domain. No setup, no code.
            </EmptyCard>,
        );
    }

    const s = data.summary!;
    const p = data.previous!;
    const noVisits = s.visitors === 0;

    return shell(
        <>
            {/* Header */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
                <div style={{ flex: 1, minWidth: 220 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <span style={{ fontSize: 18, fontWeight: 700, letterSpacing: '-0.01em', color: 'var(--hv-ink)' }}>Website analytics</span>
                        <Eyebrow style={{ padding: '3px 8px', borderRadius: 999, background: 'var(--hv-mint)', color: 'var(--hv-mint-ink)' }}>BETA</Eyebrow>
                    </div>
                    <p style={{ margin: '4px 0 0', fontSize: 12.5, lineHeight: 1.6, color: 'var(--hv-body)' }}>
                        Who visits your live site and what they do there. Cookie-free: visitors are counted anonymously.
                    </p>
                </div>
                {online !== null && (
                    <span style={{ display: 'flex', alignItems: 'center', gap: 7, height: 30, padding: '0 12px', borderRadius: 999, border: '1px solid var(--hv-line)', background: 'var(--hv-card)', fontSize: 12.5, fontWeight: 600, color: 'var(--hv-ink)' }}>
                        <span aria-hidden="true" style={{ width: 8, height: 8, borderRadius: 999, background: online > 0 ? DATA : 'var(--hv-line-2)', boxShadow: online > 0 ? `0 0 0 3px color-mix(in srgb, ${DATA} 22%, transparent)` : undefined }} />
                        {online} online now
                    </span>
                )}
                <Tip label="Refresh">
                    <RoundBtn size={34} filled onClick={() => void load(range)} aria-label="Refresh analytics">
                        <span style={{ display: 'flex', animation: loading ? 'hvSpin 0.8s linear infinite' : undefined }}>{Icons.reload({})}</span>
                    </RoundBtn>
                </Tip>
            </div>

            {/* Range filter: one row, scopes everything below */}
            <div role="radiogroup" aria-label="Date range" style={{ display: 'flex', gap: 3, padding: 3, borderRadius: 999, background: 'var(--hv-page-2)', border: '1px solid var(--hv-line)', alignSelf: 'flex-start', flexWrap: 'wrap' }}>
                {RANGES.map((r) => {
                    const on = r.id === range;
                    return (
                        <button
                            key={r.id}
                            type="button"
                            role="radio"
                            aria-checked={on}
                            data-track="analytics_range"
                            data-track-label={r.id}
                            onClick={() => setRange(r.id)}
                            style={{ height: 30, padding: '0 13px', borderRadius: 999, border: 0, cursor: 'pointer', fontSize: 12.5, fontWeight: 700, background: on ? 'var(--hv-card)' : 'transparent', color: on ? 'var(--hv-ink)' : 'var(--hv-muted)', boxShadow: on ? '0 1px 2px var(--hv-shadow)' : undefined }}
                        >
                            {r.label}
                        </button>
                    );
                })}
            </div>

            {error && <p style={{ margin: 0, fontSize: 12.5, fontWeight: 600, color: 'var(--hv-danger)' }}>{error}</p>}

            {/* Refetch keeps the frame: previous render dims instead of flashing. */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16, opacity: loading ? 0.55 : 1, transition: 'opacity 0.2s ease' }}>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(160px, 42%), 1fr))', gap: 12 }}>
                    <StatTile label="Visitors" value={fmtInt(s.visitors)} delta={delta(s.visitors, p.visitors)} upIsGood />
                    <StatTile label="Page views" value={fmtInt(s.pageviews)} delta={delta(s.pageviews, p.pageviews)} upIsGood />
                    <StatTile label="Bounce rate" value={s.visits ? `${Math.round(bounceRate(s) * 100)}%` : '–'} delta={p.visits && s.visits ? delta(bounceRate(s), bounceRate(p)) : null} hint="Visits that left after one page" />
                    <StatTile label="Avg. visit time" value={s.visits ? fmtDuration(s.totaltime / s.visits) : '–'} delta={p.visits && s.visits ? delta(s.totaltime / s.visits, p.totaltime / p.visits) : null} upIsGood />
                </div>

                {noVisits && (
                    <div style={{ padding: '14px 16px', borderRadius: 14, background: 'var(--hv-mint)', color: 'var(--hv-mint-ink)', fontSize: 12.5, lineHeight: 1.6, fontWeight: 600 }}>
                        No visits in this period yet. Share your site link on WhatsApp, Instagram or Google Business, and visits will show up here within seconds.
                    </div>
                )}

                <Card title="Customer actions" subtitle="Clicks on WhatsApp, phone, Maps and email links on your site: your warmest leads.">
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(130px, 42%), 1fr))', gap: 10 }}>
                        <LeadTile label="WhatsApp chats" value={data.leads!.whatsapp} />
                        <LeadTile label="Phone calls" value={data.leads!.phone} />
                        <LeadTile label="Directions (Maps)" value={data.leads!.maps} />
                        <LeadTile label="Emails" value={data.leads!.email} />
                    </div>
                </Card>

                <TrafficCard data={data} />

                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(340px, 100%), 1fr))', gap: 16 }}>
                    <TabbedList
                        title="Pages"
                        tabs={[
                            { label: 'Most viewed', rows: data.pages!, unit: 'visitors' },
                            { label: 'Landing pages', rows: data.entries!, unit: 'visitors' },
                        ]}
                    />
                    <TabbedList
                        title="Traffic sources"
                        tabs={[
                            { label: 'Websites', rows: data.referrers!, unit: 'visitors', empty: 'No referring sites yet. Direct visits (typed link, WhatsApp, apps) are not listed.' },
                            { label: 'Campaigns', rows: data.utmCampaigns!, unit: 'visitors', empty: 'Add ?utm_campaign=promo-name to links in ads or your bio to see which campaign brings visitors.' },
                            { label: 'UTM source', rows: data.utmSources!, unit: 'visitors', empty: 'Add ?utm_source=instagram to the links you share to see each channel here.' },
                        ]}
                    />
                    <TabbedList
                        title="Location"
                        tabs={[
                            { label: 'Countries', rows: data.countries!.map((r) => ({ ...r, x: countryName(r.x) })), unit: 'visitors' },
                            { label: 'Cities', rows: data.cities!, unit: 'visitors' },
                        ]}
                    />
                    <TabbedList
                        title="Devices"
                        tabs={[
                            { label: 'Device type', rows: data.devices!.map((r) => ({ ...r, x: titleCase(r.x) })), unit: 'visitors' },
                            { label: 'Browser', rows: data.browsers!.map((r) => ({ ...r, x: titleCase(r.x) })), unit: 'visitors' },
                        ]}
                    />
                </div>

                <HeatmapCard weekly={data.weekly!} />
            </div>
        </>,
    );
};

/* ------------------------------------------------------------------ pieces */

const Card: React.FC<{ title: string; subtitle?: string; right?: React.ReactNode; children: React.ReactNode }> = ({ title, subtitle, right, children }) => (
    <section style={{ borderRadius: 16, border: '1px solid var(--hv-line)', background: 'var(--hv-card)', padding: 18, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, marginBottom: 14 }}>
            <div style={{ flex: 1, minWidth: 0 }}>
                <h3 style={{ margin: 0, fontSize: 14, fontWeight: 700, letterSpacing: '-0.01em', color: 'var(--hv-ink)' }}>{title}</h3>
                {subtitle && <p style={{ margin: '3px 0 0', fontSize: 12, lineHeight: 1.55, color: 'var(--hv-muted)' }}>{subtitle}</p>}
            </div>
            {right}
        </div>
        {children}
    </section>
);

type Delta = { pct: number } | null;

const StatTile: React.FC<{ label: string; value: string; delta: Delta; upIsGood?: boolean; hint?: string }> = ({ label, value, delta: d, upIsGood, hint }) => {
    const good = d ? (d.pct >= 0) === !!upIsGood : false;
    return (
        <div title={hint} style={{ borderRadius: 16, border: '1px solid var(--hv-line)', background: 'var(--hv-card)', padding: '16px 18px' }}>
            <div style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--hv-body)' }}>{label}</div>
            <div style={{ marginTop: 6, fontSize: 28, fontWeight: 700, letterSpacing: '-0.02em', color: 'var(--hv-ink)' }}>{value}</div>
            <div style={{ marginTop: 4, minHeight: 18, fontSize: 12, fontWeight: 600, color: d && Math.round(d.pct) !== 0 ? (good ? 'var(--hv-mint-ink)' : 'var(--hv-danger)') : 'var(--hv-muted)' }}>
                {d ? (
                    <>
                        <span aria-hidden="true">{Math.round(d.pct) === 0 ? '–' : d.pct > 0 ? '▲' : '▼'} </span>
                        {Math.abs(Math.round(d.pct))}% <span style={{ color: 'var(--hv-muted)', fontWeight: 500 }}>vs previous period</span>
                    </>
                ) : (
                    <span style={{ fontWeight: 500 }}>No earlier data to compare</span>
                )}
            </div>
        </div>
    );
};

const LeadTile: React.FC<{ label: string; value: number }> = ({ label, value }) => (
    <div style={{ borderRadius: 12, background: 'var(--hv-page-2)', padding: '12px 14px' }}>
        <div style={{ fontSize: 22, fontWeight: 700, letterSpacing: '-0.02em', color: 'var(--hv-ink)' }}>{fmtInt(value)}</div>
        <div style={{ marginTop: 2, fontSize: 12, fontWeight: 600, color: 'var(--hv-body)' }}>{label}</div>
    </div>
);

/** Segmented tabs inside a card (Pages: most viewed / landing…). */
const CardTabs: React.FC<{ labels: string[]; active: number; onChange: (i: number) => void }> = ({ labels, active, onChange }) => (
    <div role="tablist" style={{ display: 'flex', gap: 2, padding: 2, borderRadius: 999, background: 'var(--hv-page-2)', flexWrap: 'wrap' }}>
        {labels.map((l, i) => (
            <button
                key={l}
                type="button"
                role="tab"
                aria-selected={i === active}
                onClick={() => onChange(i)}
                style={{ height: 26, padding: '0 10px', borderRadius: 999, border: 0, cursor: 'pointer', fontSize: 11.5, fontWeight: 700, background: i === active ? 'var(--hv-card)' : 'transparent', color: i === active ? 'var(--hv-ink)' : 'var(--hv-muted)' }}
            >
                {l}
            </button>
        ))}
    </div>
);

/** A ranked list: label + value, with a light wash bar showing the share of the top row. */
const TabbedList: React.FC<{ title: string; tabs: { label: string; rows: Metric[]; unit: string; empty?: string }[] }> = ({ title, tabs }) => {
    const [i, setI] = React.useState(0);
    const tab = tabs[i];
    const max = Math.max(1, ...tab.rows.map((r) => r.y));
    return (
        <Card title={title}>
            {tabs.length > 1 && (
                <div style={{ display: 'flex', marginTop: -4, marginBottom: 10 }}>
                    <CardTabs labels={tabs.map((t) => t.label)} active={i} onChange={setI} />
                </div>
            )}
            {tab.rows.length === 0 ? (
                <p style={{ margin: 0, padding: '18px 0', fontSize: 12.5, lineHeight: 1.6, color: 'var(--hv-muted)' }}>{tab.empty || 'No data in this period yet.'}</p>
            ) : (
                <table style={{ width: '100%', borderCollapse: 'separate', borderSpacing: '0 4px', tableLayout: 'fixed' }}>
                    <thead>
                        <tr>
                            <th style={thStyle('left')}>{tab.label}</th>
                            <th style={{ ...thStyle('right'), width: 80 }}>{titleCase(tab.unit)}</th>
                        </tr>
                    </thead>
                    <tbody>
                        {tab.rows.map((r) => (
                            <tr key={r.x}>
                                <td style={{ position: 'relative', padding: '7px 10px', fontSize: 12.5, color: 'var(--hv-ink)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={r.x}>
                                    <span aria-hidden="true" style={{ position: 'absolute', inset: 0, width: `${(r.y / max) * 100}%`, borderRadius: 6, background: `color-mix(in srgb, ${DATA} 14%, transparent)` }} />
                                    <span style={{ position: 'relative' }}>{r.x}</span>
                                </td>
                                <td style={{ padding: '7px 2px 7px 10px', textAlign: 'right', fontSize: 12.5, fontWeight: 700, color: 'var(--hv-ink)', fontVariantNumeric: 'tabular-nums' }}>{fmtInt(r.y)}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            )}
        </Card>
    );
};

const thStyle = (align: 'left' | 'right'): React.CSSProperties => ({ textAlign: align, padding: '0 2px 4px 10px', fontFamily: FONT_MONO, fontSize: 9.5, fontWeight: 400, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'var(--hv-muted)' });

/* ---------------------------------------------------------- traffic chart */

const TrafficCard: React.FC<{ data: AnalyticsDashboard }> = ({ data }) => {
    const [measure, setMeasure] = React.useState<0 | 1>(0); // 0 visitors, 1 page views
    const [showTable, setShowTable] = React.useState(false);
    const buckets = React.useMemo(() => bucketKeys(data.startAt!, data.endAt!, data.unit!, data.timezone!), [data]);
    const rows = React.useMemo(() => {
        const src = measure === 0 ? data.series!.sessions : data.series!.pageviews;
        const by = new Map(src.map((m) => [keyOf(m.x, data.unit!), m.y]));
        return buckets.map((k) => ({ key: k, label: bucketLabel(k, data.unit!), y: Number(by.get(k) ?? 0) }));
    }, [buckets, data, measure]);

    return (
        <Card
            title={measure === 0 ? 'Visitors over time' : 'Page views over time'}
            subtitle={`Per ${data.unit}, in your time zone (${data.timezone}).`}
            right={<CardTabs labels={['Visitors', 'Page views']} active={measure} onChange={(i) => setMeasure(i as 0 | 1)} />}
        >
            <ColumnChart rows={rows} unit={measure === 0 ? 'visitors' : 'page views'} />
            <button type="button" onClick={() => setShowTable((v) => !v)} className="hv-h-underline" style={{ marginTop: 10, border: 0, padding: 0, background: 'transparent', cursor: 'pointer', fontSize: 12, fontWeight: 600, color: 'var(--hv-muted)' }}>
                {showTable ? 'Hide table' : 'Show as table'}
            </button>
            {showTable && (
                <div style={{ marginTop: 8, maxHeight: 240, overflow: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                        <tbody>
                            {rows.map((r) => (
                                <tr key={r.key} style={{ borderTop: '1px solid var(--hv-line)' }}>
                                    <td style={{ padding: '5px 4px', fontSize: 12, color: 'var(--hv-body)' }}>{r.label}</td>
                                    <td style={{ padding: '5px 4px', fontSize: 12, fontWeight: 700, textAlign: 'right', color: 'var(--hv-ink)', fontVariantNumeric: 'tabular-nums' }}>{fmtInt(r.y)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </Card>
    );
};

const CHART_H = 200;
const PAD_L = 36;
const PAD_B = 22;

/** Single-series column chart with per-column hover/focus tooltip. */
const ColumnChart: React.FC<{ rows: { key: string; label: string; y: number }[]; unit: string }> = ({ rows, unit }) => {
    const ref = React.useRef<HTMLDivElement>(null);
    const [w, setW] = React.useState(600);
    const [hover, setHover] = React.useState<number | null>(null);

    React.useEffect(() => {
        const el = ref.current;
        if (!el) return;
        const ro = new ResizeObserver(([e]) => setW(Math.max(240, e.contentRect.width)));
        ro.observe(el);
        return () => ro.disconnect();
    }, []);

    const max = niceMax(Math.max(0, ...rows.map((r) => r.y)));
    const plotW = w - PAD_L;
    const plotH = CHART_H - PAD_B;
    const band = plotW / Math.max(1, rows.length);
    const barW = Math.max(2, Math.min(24, band - 2)); // 2px surface gap between touching bars
    const ticks = [0, max / 2, max];
    const labelEvery = Math.ceil(rows.length / Math.max(1, Math.floor(plotW / 64)));
    const h = hover !== null ? rows[hover] : null;

    return (
        <div ref={ref} style={{ position: 'relative', width: '100%' }}>
            <svg width={w} height={CHART_H} role="img" aria-label={`${unit} per period`} style={{ display: 'block', overflow: 'visible' }}>
                {ticks.map((t) => {
                    const y = plotH - (t / max) * plotH;
                    return (
                        <g key={t}>
                            <line x1={PAD_L} x2={w} y1={y} y2={y} stroke="var(--hv-line)" strokeWidth={1} />
                            <text x={PAD_L - 8} y={y + 3.5} textAnchor="end" fontSize={10.5} fill="var(--hv-muted)" style={{ fontVariantNumeric: 'tabular-nums' }}>{fmtCompact(t)}</text>
                        </g>
                    );
                })}
                {rows.map((r, i) => {
                    const x = PAD_L + i * band + (band - barW) / 2;
                    const bh = max ? (r.y / max) * plotH : 0;
                    const y = plotH - bh;
                    const rad = Math.min(4, barW / 2, bh);
                    return (
                        <g key={r.key}>
                            {bh > 0 && (
                                <path
                                    d={`M${x},${plotH} V${y + rad} Q${x},${y} ${x + rad},${y} H${x + barW - rad} Q${x + barW},${y} ${x + barW},${y + rad} V${plotH} Z`}
                                    fill={DATA}
                                    opacity={hover === null || hover === i ? 1 : 0.45}
                                />
                            )}
                            {i % labelEvery === 0 && (
                                <text x={PAD_L + i * band + band / 2} y={CHART_H - 6} textAnchor="middle" fontSize={10.5} fill="var(--hv-muted)">{r.label}</text>
                            )}
                            {/* Hit target: the whole band, full height (bigger than the mark). */}
                            <rect
                                x={PAD_L + i * band}
                                y={0}
                                width={band}
                                height={plotH}
                                fill="transparent"
                                tabIndex={0}
                                aria-label={`${r.label}: ${r.y} ${unit}`}
                                onPointerEnter={() => setHover(i)}
                                onPointerLeave={() => setHover(null)}
                                onFocus={() => setHover(i)}
                                onBlur={() => setHover(null)}
                                style={{ outline: 'none' }}
                            />
                        </g>
                    );
                })}
            </svg>
            {h && hover !== null && (
                <div
                    role="tooltip"
                    style={{
                        position: 'absolute',
                        top: 0,
                        left: Math.min(Math.max(PAD_L + hover * band + band / 2, 70), w - 70),
                        transform: 'translateX(-50%)',
                        pointerEvents: 'none',
                        padding: '7px 10px',
                        borderRadius: 10,
                        background: 'var(--hv-ink)',
                        color: 'var(--hv-page)',
                        whiteSpace: 'nowrap',
                        boxShadow: '0 10px 24px -12px rgba(0,0,0,0.5)',
                    }}
                >
                    <div style={{ fontSize: 14, fontWeight: 700 }}>{fmtInt(h.y)}</div>
                    <div style={{ fontSize: 11, opacity: 0.8 }}>{unit} · {h.label}</div>
                </div>
            )}
        </div>
    );
};

/* ---------------------------------------------------------------- heatmap */

const DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const DAY_INDEX = [1, 2, 3, 4, 5, 6, 0]; // Umami rows are Sunday-first

const HeatmapCard: React.FC<{ weekly: number[][] }> = ({ weekly }) => {
    const [hover, setHover] = React.useState<{ d: number; h: number } | null>(null);
    const rows = DAY_INDEX.map((i) => weekly[i] ?? new Array(24).fill(0));
    const max = Math.max(0, ...rows.flat());
    // Busiest slot, said in words so the insight never depends on reading color.
    let best: { d: number; h: number; v: number } | null = null;
    rows.forEach((r, d) => r.forEach((v, h) => { if (v > 0 && (!best || v > best.v)) best = { d, h, v }; }));
    const b = best as { d: number; h: number; v: number } | null;

    return (
        <Card
            title="Busiest times"
            subtitle={b ? `Most visitors arrive on ${DAYS[b.d]} around ${hourLabel(b.h)}. Post promos and reply to chats just before then.` : 'Visitors by day and hour. It fills in as people visit.'}
            right={hover ? <Eyebrow>{DAYS[hover.d]} {hourLabel(hover.h)} · {rows[hover.d][hover.h]} visitors</Eyebrow> : undefined}
        >
            <div style={{ overflowX: 'auto' }}>
                <div style={{ display: 'grid', gridTemplateColumns: '34px repeat(24, minmax(14px, 1fr))', gap: 2, minWidth: 420 }}>
                    {rows.map((r, d) => (
                        <React.Fragment key={d}>
                            <div style={{ fontSize: 10.5, color: 'var(--hv-muted)', alignSelf: 'center' }}>{DAYS[d]}</div>
                            {r.map((v, h) => (
                                <div
                                    key={h}
                                    tabIndex={0}
                                    aria-label={`${DAYS[d]} ${hourLabel(h)}: ${v} visitors`}
                                    onPointerEnter={() => setHover({ d, h })}
                                    onPointerLeave={() => setHover(null)}
                                    onFocus={() => setHover({ d, h })}
                                    onBlur={() => setHover(null)}
                                    style={{
                                        height: 18,
                                        borderRadius: 4,
                                        background: heat(v, max),
                                        outline: hover && hover.d === d && hover.h === h ? '2px solid var(--hv-ink)' : undefined,
                                        outlineOffset: -1,
                                    }}
                                />
                            ))}
                        </React.Fragment>
                    ))}
                    <div />
                    {Array.from({ length: 24 }, (_, h) => (
                        <div key={h} style={{ fontSize: 9.5, color: 'var(--hv-muted)', textAlign: 'center' }}>{h % 3 === 0 ? String(h).padStart(2, '0') : ''}</div>
                    ))}
                </div>
            </div>
        </Card>
    );
};

/** Sequential scale: one hue, light to dark, in 5 steps (empty cells stay neutral). */
function heat(v: number, max: number): string {
    if (!v || !max) return 'var(--hv-page-2)';
    const step = Math.min(4, Math.floor((v / max) * 5));
    return `color-mix(in srgb, ${DATA} ${[22, 40, 58, 78, 100][step]}%, var(--hv-card))`;
}

/* ---------------------------------------------------------------- helpers */

function delta(now: number, before: number): Delta {
    if (!before) return null;
    return { pct: ((now - before) / before) * 100 };
}

function bounceRate(s: StatsSummary): number {
    return s.visits ? Math.min(1, s.bounces / s.visits) : 0;
}

function fmtInt(n: number): string {
    return n >= 10000 ? fmtCompact(n) : new Intl.NumberFormat('en-US').format(Math.round(n));
}

function fmtCompact(n: number): string {
    return new Intl.NumberFormat('en-US', { notation: 'compact', maximumFractionDigits: 1 }).format(n);
}

function fmtDuration(sec: number): string {
    const s = Math.round(sec);
    if (s < 60) return `${s}s`;
    const m = Math.floor(s / 60);
    return m < 60 ? `${m}m ${s % 60}s` : `${Math.floor(m / 60)}h ${m % 60}m`;
}

/** Round an axis max up to 1/2/5 x 10^n so the ticks are clean numbers. */
function niceMax(v: number): number {
    if (v <= 4) return 4;
    const p = Math.pow(10, Math.floor(Math.log10(v)));
    for (const m of [1, 2, 5, 10]) if (m * p >= v) return m * p;
    return 10 * p;
}

function hourLabel(h: number): string {
    return `${String(h).padStart(2, '0')}:00`;
}

function titleCase(s: string): string {
    return s ? s.charAt(0).toUpperCase() + s.slice(1) : s;
}

let regionNames: Intl.DisplayNames | null = null;
function countryName(code: string): string {
    try {
        regionNames ??= new Intl.DisplayNames(['en'], { type: 'region' });
        return regionNames.of(code.toUpperCase()) || code;
    } catch {
        return code;
    }
}

/**
 * Umami labels buckets with the wall-clock time in the requested zone, written
 * as "YYYY-MM-DDTHH:00:00Z" (the Z is cosmetic). Compare on the unit's prefix.
 */
function keyOf(x: string, unit: string): string {
    return x.slice(0, unit === 'hour' ? 13 : unit === 'day' ? 10 : 7);
}

/** Every bucket in [startAt, endAt] so empty hours/days still get a column. */
function bucketKeys(startAt: number, endAt: number, unit: string, tz: string): string[] {
    const fmt = new Intl.DateTimeFormat('en-CA', { timeZone: tz, year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', hourCycle: 'h23' });
    const step = unit === 'hour' ? 3600_000 : 86400_000 / 4; // quarter days survive DST shifts
    const keys: string[] = [];
    for (let t = startAt; t <= endAt; t += step) {
        const parts = Object.fromEntries(fmt.formatToParts(new Date(t)).map((p) => [p.type, p.value]));
        const k = unit === 'hour' ? `${parts.year}-${parts.month}-${parts.day}T${parts.hour}` : unit === 'day' ? `${parts.year}-${parts.month}-${parts.day}` : `${parts.year}-${parts.month}`;
        if (keys[keys.length - 1] !== k) keys.push(k);
    }
    return keys;
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
function bucketLabel(key: string, unit: string): string {
    const [y, m, rest] = key.split('-');
    if (unit === 'month') return `${MONTHS[Number(m) - 1]} ${y.slice(2)}`;
    const d = Number(rest.slice(0, 2));
    if (unit === 'day') return `${d} ${MONTHS[Number(m) - 1]}`;
    return `${rest.slice(3, 5)}:00`;
}
